import { useDispatch, useSelector } from 'react-redux';
import { Form, Formik, FormikHelpers, FormikProps } from 'formik';
import React, { useContext, useRef } from 'react';
import Banner from '@cpg-its/ads.containers.banner';
import { useHistory } from 'react-router';
import { Hidden } from '@material-ui/core';
import { PageContainer } from '../../../../../global/components/layout/page-container';
import { MainContent } from '../../../../../global/components/layout/main-content-container';
import { PageHeader } from '../../../../../global/components/layout/page-header';
import { useApiResponseError } from '../../../../../global/hooks/useApiError';
import { useGpsType } from '../../../../../global/hooks/useGpsType';
import { SideBarContent } from '../../../../../global/components/layout/sidebar-content';
import { GuidedProcessSupportBlock } from '../../../../../global/components/guided-process-support-block';
import { FormWrapper } from '../../../../../global/components/form-wrapper';
import { TcsmStates } from '../../../../../root/tcsm-model';
import { LabelSettingProps } from '../../../../../global/models/epi-model';
import { NewHirePersonalInfoFields } from '../../new-hire/components/new-hire-personal-info-fields';
import { NewHireFormValues, getInitialNewHireValues } from '../../new-hire/models/new-hire-form-model';
import { ParticipantEnrollmentSettings } from '../models/epi-props';
import { GpsType } from '../../../../../global/components/layout/layout-models';
import { setCurrentEmployee } from '../../employment/employment-slice';
import { PersonSearchResults } from '../../person-search/person-search-results';
import { matchedPersonField } from '../../../../account-info/models/form-models';
import { PersonSearchContext, PersonSearchState, SearchPerson } from '../../person-search/person-search-model';
import { PersonSearchToastCombo } from '../../person-search/person-search-toast-combo';
import { NewHireStep } from '../../new-hire/models/new-hire-model';
import { apiErrorClear } from '../../../../../global/ducks/base-api';
import { OSCRRService } from '../../person-search/oscrr-helpers';
import { noSelectPersonId } from '../../../models/arc-epi-props';
import { BillingAccount } from '../../billing-accounts/billing-accounts-models';
import { useParticipantState } from '../../participants/useParticipantState';
import { ParticipantsEnrollmentRoutes } from '../models/routes';
import { ArcRoutes } from '../../../constants/routes';
import { useBasePath } from '../../../../../global/hooks/useBasePath';
import { OnSubmit, getNewParticipantValidationSchema } from './helper/new-participant-helper';
import { BaseApiState } from '../../../../../global/models/api-model';
import { WysiwygWithReactLink } from '../../../../../global/components/wysiwyg-with-react-link';
import { ContentSpacer } from '../../../../../global/components/layout/generic-content-spacer';

interface Props {
    currentStep: NewHireStep;
}

export function CreateNewParticipantForm(props: Props) {

    const { currentStep } = props;

    const groupName = matchedPersonField;
    const personSearchKey = useContext(PersonSearchContext);
    const searchResultsRef = useRef<HTMLDivElement>();
    const apiState = useSelector<TcsmStates, BaseApiState>(state => state.apiState);
    const personSearchState = useSelector<TcsmStates, PersonSearchState>(state => state.personSearchState[personSearchKey]);
    const labelSettings = useSelector<TcsmStates, LabelSettingProps>(state => state?.epiProps?.labelSettingState?.epiContent);
    const hasSearchResults: boolean = personSearchState?.searchResults?.length > 0;
    const initialValues = getInitialNewHireValues(null, false);
    const apiErrors = useApiResponseError();
    const gpsItem = useGpsType(GpsType.CREATE_NEW_PARTICIPANT);
    const participantProps = useSelector<TcsmStates, ParticipantEnrollmentSettings>(state => state?.epiProps?.participantEnrollmentSettingsState?.epiContent);
    const dispatch = useDispatch();
    const history = useHistory();
    const selectedBillingAccount = useSelector<TcsmStates, BillingAccount>(state => state.billingAccountsState?.currentBillingAccount);
    const participantState = useParticipantState(selectedBillingAccount?.group_id, selectedBillingAccount?.account_number);
    const defaultValidationSchema = getNewParticipantValidationSchema(labelSettings, hasSearchResults);
    const existingRecord = useBasePath(`${ArcRoutes.ArcRoot}${ArcRoutes.ParticipantEnrollment}${ParticipantsEnrollmentRoutes.ExistingParticipantRecord}`);
    const whoWillEnrollPath = useBasePath(`${ArcRoutes.ArcRoot}${ArcRoutes.ParticipantEnrollment}${ParticipantsEnrollmentRoutes.WhoWillEnroll}`);


    const onSuccess = (res) => {
        const formatted = {
            guid: res?.data?.person_guid,
            ...res?.data,
        };
        dispatch(setCurrentEmployee(formatted));
        history.push(whoWillEnrollPath);
    };

    const onNavigateToTop = () => {
        window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
    };

    const onPersonSelect = (person: SearchPerson, formProps: FormikProps<NewHireFormValues>) => {
        dispatch(apiErrorClear());
        formProps.setTouched({});

        // selected search person is internal
        const existingEmployees = participantState?.billingAccountDetail?.covered_people.find(employee => employee.guid === person.guid);

        dispatch(setCurrentEmployee(existingEmployees ?? person));
        history.push(existingEmployees ? existingRecord : whoWillEnrollPath);

    };

    async function onInternalSubmit(values: NewHireFormValues, formikHelpers: FormikHelpers<NewHireFormValues>) {

        const formProps: FormikProps<NewHireFormValues> = formikHelpers as FormikProps<NewHireFormValues>;

        if (await OSCRRService.SearchHasNewResultsAsync(apiState, personSearchState, values[groupName])) return;

        if (values) {
            const selectedPersonId = values[groupName];

            if (!(hasSearchResults && selectedPersonId) || selectedPersonId === noSelectPersonId) {
                OnSubmit(values, initialValues, dispatch, onSuccess);
                return;
            }

            const selectedPersonGrid = personSearchState?.searchResults?.find(employee => employee.guid === selectedPersonId);
            if (selectedPersonGrid) {
                onPersonSelect(selectedPersonGrid, formProps);
            }
        }
    }

    const cancelAction = () => {

        history.goBack();
    };


    return (
        <>
            <Formik
                initialValues={initialValues}
                onSubmit={(values, formikHelpers) => onInternalSubmit(values, formikHelpers)}
                validationSchema={defaultValidationSchema}
                enableReinitialize
                initialErrors={apiErrors}
            >
                {(formProps: FormikProps<any>) => {
                    return (
                        <PageContainer>
                            <MainContent>
                                <PageHeader
                                    headerText={participantProps?.createNewParticipantPageTitle}
                                />
                                {participantProps?.createNewParticipantPageDescription && <ContentSpacer>
                                    <Banner
                                        banner={{
                                            type: 'warning',
                                            label: <WysiwygWithReactLink content={participantProps?.createNewParticipantPageDescription} />,
                                        }}
                                    />
                                </ContentSpacer>}
                                <Hidden mdUp>
                                    <SideBarContent>
                                        <GuidedProcessSupportBlock title={gpsItem?.title} contents={gpsItem?.contents} />
                                    </SideBarContent>
                                </Hidden>
                                <Form>
                                    <FormWrapper
                                        formikProps={formProps}
                                        personSearchToast={
                                            <PersonSearchToastCombo
                                                searchResultsRef={searchResultsRef}
                                                renderResultsMessaging={currentStep === NewHireStep.PersonalInfo && personSearchState.searchResults?.length > 0}
                                            />
                                        }
                                        personSearchResults={
                                            <PersonSearchResults
                                                groupName={groupName}
                                                results={personSearchState?.searchResults}
                                                backToTopAction={onNavigateToTop}
                                                resultsRef={searchResultsRef} />
                                        }
                                        cancelText={participantProps?.cancelCreateNewParticipantButtonText}
                                        submitText={participantProps?.submitCreateNewParticipantButtonText}
                                        onCancel={cancelAction}
                                    >
                                        <NewHirePersonalInfoFields formProps={formProps} labelSetting={labelSettings} />
                                    </FormWrapper>
                                </Form>

                            </MainContent>
                            <Hidden smDown>
                                <SideBarContent>
                                    <GuidedProcessSupportBlock title={gpsItem?.title} contents={gpsItem?.contents} />
                                </SideBarContent>
                            </Hidden>
                        </PageContainer>

                    );
                }}

            </Formik>
        </>
    );
}
